import { useState, useEffect, useRef, useCallback } from 'react';
import { searchPatients, previewPatientMerge, mergePatients } from '../services/api';
import './Admin.css';

// Admin tool for consolidating a duplicate patient account into a surviving one.
// The point of the system is ONE patient account with many facilities linked to
// it; when a biller accidentally re-creates a patient under another facility this
// merges the two so all claims/payments/etc. live under a single account.

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString();
};

function PatientPicker({ role, selected, onSelect, onClear, disabledId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const isSurvivor = role === 'survivor';

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (selected || !query.trim()) {
      setResults([]);
      return;
    }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // Archived accounts are included on purpose: a duplicate is very often
        // already archived, and the merge is built to take it. Picking an archived
        // account as the SURVIVOR is refused by the server with an explanation,
        // which is more use to a biller than the account simply not being found.
        const res = await searchPatients(query.trim(), undefined, { includeArchived: true });
        if (active) {
          setResults(res.data || []);
          setOpen(true);
        }
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [query, selected]);

  if (selected) {
    return (
      <div className={`merge-card merge-card--${role}`}>
        <div className="merge-card-tag">{isSurvivor ? 'Keeping this account' : 'Merging & archiving'}</div>
        <div className="merge-card-name">{selected.first_name} {selected.last_name}</div>
        <div className="merge-card-meta">
          <span>ID #{selected.patient_id}</span>
          <span>DOB {fmtDate(selected.date_of_birth)}</span>
          {selected.emr_patient_id && <span>MRN {selected.emr_patient_id}</span>}
        </div>
        <div className="merge-card-facility">{selected.facility_name || 'No home facility'}</div>
        {selected.archived_at && <div className="merge-card-archived">Archived</div>}
        <button className="merge-card-change" onClick={onClear}>Change</button>
      </div>
    );
  }

  return (
    <div className="merge-picker" ref={boxRef}>
      <label className="merge-picker-label">
        {isSurvivor ? 'Surviving account (keep)' : 'Duplicate account (merge in & archive)'}
      </label>
      <div className="admin-search-wrapper" style={{ maxWidth: '100%' }}>
        <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          className="admin-search"
          placeholder="Search by name or MRN…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
        />
      </div>
      {open && (loading || results.length > 0 || query.trim()) && (
        <div className="merge-results">
          {loading && <div className="merge-result-empty">Searching…</div>}
          {!loading && results.length === 0 && <div className="merge-result-empty">No patients found</div>}
          {!loading && results.map((p) => {
            const disabled = p.patient_id === disabledId;
            return (
              <button
                key={p.patient_id}
                className="merge-result"
                disabled={disabled}
                onClick={() => { onSelect(p); setOpen(false); setQuery(''); }}
                title={disabled ? 'Already selected as the other account' : undefined}
              >
                <span className="merge-result-name">{p.first_name} {p.last_name}</span>
                <span className="merge-result-meta">
                  #{p.patient_id} · DOB {fmtDate(p.date_of_birth)} · {p.facility_name || '—'}
                  {p.archived_at ? ' · Archived' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MergePatients() {
  const [survivor, setSurvivor] = useState(null);
  const [loser, setLoser] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [msg, setMsg] = useState(null);

  const resetPreview = () => { setPreview(null); setMsg(null); };

  const loadPreview = useCallback(async () => {
    if (!survivor || !loser) return;
    setPreviewLoading(true);
    setMsg(null);
    try {
      const res = await previewPatientMerge(survivor.patient_id, loser.patient_id);
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      setMsg({ type: 'error', text: err.response?.data?.error?.message || 'Failed to build merge preview.' });
    } finally {
      setPreviewLoading(false);
    }
  }, [survivor, loser]);

  // Auto-refresh the preview whenever a fresh, valid pair is selected.
  useEffect(() => {
    if (survivor && loser && survivor.patient_id !== loser.patient_id) {
      loadPreview();
    } else {
      setPreview(null);
    }
  }, [survivor, loser, loadPreview]);

  const swap = () => {
    setSurvivor(loser);
    setLoser(survivor);
  };

  const handleMerge = async () => {
    setMerging(true);
    setMsg(null);
    try {
      const res = await mergePatients(survivor.patient_id, loser.patient_id, reason.trim());
      setConfirmOpen(false);
      const moved = res.data?.moved;
      const identity = moved ? ` Moved ${moved.source_ids_moved ?? 0} source ID(s) and ${moved.reviews_moved ?? 0} review item(s).` : '';
      setMsg({ type: 'success', text: (res.message || 'Patients merged successfully.') + identity });
      // Clear the workspace — the loser no longer exists as an active account.
      setSurvivor(null);
      setLoser(null);
      setPreview(null);
      setReason('');
    } catch (err) {
      setConfirmOpen(false);
      setMsg({ type: 'error', text: err.response?.data?.error?.message || 'Failed to merge patient accounts.' });
    } finally {
      setMerging(false);
    }
  };

  const samePatient = survivor && loser && survivor.patient_id === loser.patient_id;
  const canMerge = preview && !samePatient && reason.trim().length > 0 && !previewLoading;
  const m = preview?.moves;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Merge Patient Accounts</h1>
          <p>Consolidate a duplicate patient into one account — all claims, payments, statements, plans, MRNs and insurance move together, atomically.</p>
        </div>
      </div>

      {msg && (
        <div className={`admin-modal-alert ${msg.type}`} style={{ marginBottom: 20 }}>
          {msg.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="merge-grid">
        <PatientPicker
          role="survivor"
          selected={survivor}
          onSelect={(p) => { setSurvivor(p); resetPreview(); }}
          onClear={() => { setSurvivor(null); resetPreview(); }}
          disabledId={loser?.patient_id}
        />

        <div className="merge-arrow" aria-hidden="true">
          <button className="merge-swap" onClick={swap} disabled={!survivor && !loser} title="Swap which account is kept">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="17 1 21 5 17 9" />
              <path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <polyline points="7 23 3 19 7 15" />
              <path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
          </button>
        </div>

        <PatientPicker
          role="loser"
          selected={loser}
          onSelect={(p) => { setLoser(p); resetPreview(); }}
          onClear={() => { setLoser(null); resetPreview(); }}
          disabledId={survivor?.patient_id}
        />
      </div>

      {samePatient && (
        <div className="merge-inline-warning">Pick two different accounts to merge.</div>
      )}

      {previewLoading && <div className="merge-preview-loading">Analyzing accounts…</div>}

      {preview && !samePatient && (
        <div className="merge-preview">
          <h2 className="merge-preview-title">
            What will move into <strong>{preview.survivor.name}</strong> (#{preview.survivor.patient_id})
          </h2>

          <div className="merge-stats">
            <MergeStat label="Claims" value={m.claims} />
            <MergeStat label="Self-pay payments" value={m.patient_payments} />
            <MergeStat label="Statements" value={m.patient_statements} />
            <MergeStat label="Payment plans" value={m.payment_plans} />
            <MergeStat label="Facility MRNs" value={m.mrns_moved} />
            <MergeStat label="Insurance coverages" value={m.insurances_moved} />
            <MergeStat label="Source IDs" value={m.source_ids_moved ?? 0} />
            <MergeStat label="Review items" value={m.reviews_moved ?? 0} />
          </div>

          {preview.insurance_resequence.length > 0 && (
            <div className="merge-detail-block">
              <h3>Insurance re-sequencing</h3>
              <p className="merge-detail-note">
                {preview.survivor.name}&apos;s existing coverages stay where they are; the duplicate&apos;s fill
                the free primary/secondary/tertiary slots below them. Only three coverages can be
                active, so any beyond that — and anything already archived on the duplicate — are
                filed as archived coverage.
              </p>
              <ul className="merge-detail-list">
                {preview.insurance_resequence.map((r) => (
                  <li key={r.patient_insurance_id}>
                    {r.insurance_provider} <span className="merge-muted">({r.policy_number || 'no policy #'})</span>
                    {r.to_archived ? (
                      <span className="merge-muted">
                        {' → archived coverage'}
                        {r.from_archived ? ' (already archived — stays archived)' : ' (no active slot free)'}
                      </span>
                    ) : (
                      <span className="merge-muted"> → coverage order {r.to_order}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.mrn_conflicts.length > 0 && (
            <div className="merge-detail-block">
              <h3>MRN conflicts</h3>
              <p className="merge-detail-note">Both accounts have an MRN at these facilities — the surviving account&apos;s MRN is kept and the duplicate&apos;s is dropped.</p>
              <ul className="merge-detail-list">
                {preview.mrn_conflicts.map((c) => (
                  <li key={c.facility_id}>
                    {c.facility_name}: keep <strong>{c.survivor_mrn}</strong>, drop <span className="merge-strike">{c.loser_mrn}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.warnings.length > 0 && (
            <div className="merge-warnings">
              {preview.warnings.map((w, i) => (
                <div className="merge-warning" key={i}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          <div className="merge-reason">
            <label htmlFor="merge-reason">Reason for merge <span className="merge-required">*</span></label>
            <textarea
              id="merge-reason"
              className="merge-reason-input"
              rows={3}
              placeholder="e.g. Duplicate account created under the wrong facility on intake."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="merge-actions">
            <button className="admin-confirm-danger" disabled={!canMerge} onClick={() => setConfirmOpen(true)}>
              Merge accounts
            </button>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Merge these accounts?</h3>
            <p>
              All records from <strong>{loser.first_name} {loser.last_name}</strong> (#{loser.patient_id}) will be moved to <strong>{survivor.first_name} {survivor.last_name}</strong> (#{survivor.patient_id}),
              and the duplicate account will be archived. This runs as a single all-or-nothing transaction and cannot be undone from here.
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setConfirmOpen(false)} disabled={merging}>
                Cancel
              </button>
              <button className="admin-confirm-danger" onClick={handleMerge} disabled={merging}>
                {merging && <span className="admin-modal-spinner" />}
                {merging ? 'Merging…' : 'Confirm merge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MergeStat({ label, value }) {
  return (
    <div className={`merge-stat ${value > 0 ? 'has-value' : ''}`}>
      <span className="merge-stat-value">{value}</span>
      <span className="merge-stat-label">{label}</span>
    </div>
  );
}

export default MergePatients;
